import {
    Box,
    TextField as MuiTextField,
    TextFieldProps as MuiTextFieldProps,
    SxProps,
    Theme,
    Typography,
    styled,
} from '@mui/material';
import React, { HTMLInputTypeAttribute } from 'react';
import { FieldError } from 'react-hook-form';
import { EColors, getColor } from 'theme';

const StyledTextField = styled(MuiTextField)<MuiTextFieldProps & { error: boolean }>(({ error }) => ({
    flex: 1,
    boxSizing: 'border-box',
    width: '100%',
    fontSize: '.875rem',
    '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${error ? getColor(EColors.red) : getColor(EColors.v3Border)}`,
        borderRadius: '12px',
    },
    '& .MuiInputBase-root': {
        backgroundColor: getColor(EColors.v3Background),
        borderRadius: '12px',
        paddingLeft: '16px',
        fontSize: '0.875rem',
    },

    '& .MuiInputBase-root > textarea': {
        fontSize: '0.875rem',
    },

    '& .MuiFormLabel-root': {
        transform: 'translate(16px, 14px)',
        fontSize: '.875rem',
        color: getColor(EColors.pureBlack, 0.5),
        '&.Mui-focused': {
            color: getColor(EColors.pureBlack, 0.6),
        },
    },

    '& .MuiFormLabel-root.MuiInputLabel-shrink': {
        color: getColor(EColors.pureBlack, 0.6),
        fontSize: '.875rem',
        transform: 'translate(9px, -50%) scale(1)',
        backgroundColor: getColor(EColors.pureWhite),
        padding: '0 5px',
        lineHeight: '1em',
    },
    '&:hover .MuiInputBase-root:hover > .MuiOutlinedInput-notchedOutline': {
        borderColor: getColor(EColors.v3Dark) + ' !important',
    },
}));

type TextFieldProps = {
    label?: string;
    placeholder?: string;
    error?: FieldError | undefined;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    value: string | number | undefined;
    disabled?: boolean | undefined;
    required?: boolean;
    name?: string;
    type?: HTMLInputTypeAttribute;
    maxRows?: number;
    minRows?: number;
    sx?: SxProps<Theme>;
    /** For a field labelled by a heading outside it instead of its own floating label. */
    'aria-labelledby'?: string;
};

export const TextArea = React.forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            label,
            error,
            type = 'text',
            placeholder,
            required = false,
            minRows = 1,
            maxRows = 3,
            sx,
            'aria-labelledby': ariaLabelledBy,
            ...field
        },
        ref
    ) => {
        return (
            <Box>
                <StyledTextField
                    sx={sx}
                    ref={ref}
                    multiline
                    type={type}
                    label={label}
                    error={!!error}
                    minRows={minRows}
                    maxRows={maxRows < minRows ? minRows : maxRows}
                    required={required}
                    value={field.value}
                    placeholder={placeholder}
                    disabled={field.disabled}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputProps={{ 'aria-labelledby': ariaLabelledBy }}
                    InputLabelProps={{
                        shrink: field.value !== '' && field.value != null,
                    }}
                />
                {!!error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                        {error.message}
                    </Typography>
                )}
            </Box>
        );
    }
);
